import React, { useState, useContext } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Link,
  Divider
} from '@mui/material';
import {
  CheckCircle as CompleteIcon,
  Cancel as DismissIcon,
  Edit as EditIcon,
  ContactPhone as ContactIcon,
  Person as PersonIcon,
  PersonOutline as LeadIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { 
  getEntityTypeLabel, 
  getTaskTypeLabel, 
  getPriorityLabel, 
  getStatusLabel,
  getPriorityColor,
  getStatusColor,
  getEntityTypeColor
} from '../../constants/tasksConstants';

const TaskDetails = ({ task, onUpdate, onEdit, onAddContact }) => {
  const { currentUser, isAdmin } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [dismissDialogOpen, setDismissDialogOpen] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [dismissalNotes, setDismissalNotes] = useState('');
  
  const canEdit = isAdmin || task.assignedTo === currentUser?.uid;
  const isCompleted = task.status === 'completed';
  const isDismissed = task.status === 'dismissed';
  const isActive = !isCompleted && !isDismissed;

  const handleCompleteTask = async () => {
    if (!completionNotes.trim()) return;
    
    setLoading(true);
    try {
      const taskRef = doc(db, 'tasks', task.id);
      const updateData = {
        status: 'completed',
        completedAt: new Date(),
        notesOnCompletion: completionNotes,
        lastUpdate: new Date()
      };
      
      await updateDoc(taskRef, updateData);
      
      if (onUpdate) {
        onUpdate({
          ...task,
          ...updateData
        });
      }
      
      setCompleteDialogOpen(false);
      setCompletionNotes('');
    } catch (error) {
      console.error('Błąd podczas oznaczania zadania jako ukończone:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismissTask = async () => {
    if (!dismissalNotes.trim()) return;
    
    setLoading(true);
    try {
      const taskRef = doc(db, 'tasks', task.id);
      const updateData = {
        status: 'dismissed',
        completedAt: new Date(),
        notesOnCompletion: dismissalNotes,
        lastUpdate: new Date()
      };
      
      await updateDoc(taskRef, updateData);
      
      if (onUpdate) {
        onUpdate({
          ...task,
          ...updateData
        });
      }
      
      setDismissDialogOpen(false);
      setDismissalNotes('');
    } catch (error) {
      console.error('Błąd podczas odrzucania zadania:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Brak daty';
    return format(date instanceof Date ? date : date.toDate(), 'dd.MM.yyyy HH:mm', { locale: pl });
  };

  return (
    <Box>
      {/* Nagłówek z podstawowymi informacjami */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Szczegóły zadania</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isActive && canEdit && (
            <>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<EditIcon />}
                onClick={onEdit}
              >
                Edytuj
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CompleteIcon />}
                onClick={() => setCompleteDialogOpen(true)}
              >
                Oznacz jako ukończone
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DismissIcon />}
                onClick={() => setDismissDialogOpen(true)}
              >
                Odrzuć
              </Button>
            </>
          )}
          {task.entityType !== 'general' && (
            <Button
              variant="outlined"
              color="info"
              startIcon={<ContactIcon />}
              onClick={() => onAddContact && onAddContact(task.entityType, task.entityId)}
            >
              Dodaj kontakt
            </Button>
          )}
        </Box>
      </Box>

      {/* Główne informacje o zadaniu */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Podstawowe informacje
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Status</Typography>
                <Chip 
                  label={getStatusLabel(task.status)} 
                  color={getStatusColor(task.status)}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Priorytet</Typography>
                <Chip 
                  label={getPriorityLabel(task.priority)} 
                  color={getPriorityColor(task.priority)}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Typ zadania</Typography>
                <Typography variant="body1">{getTaskTypeLabel(task.taskType)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Data wykonania</Typography>
                <Typography variant="body1">{formatDate(task.dueDate)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Przypisane do</Typography>
                <Typography variant="body1">{task.assignedToName || task.assignedTo}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">Utworzone przez</Typography>
                <Typography variant="body1">
                  {task.createdBy === 'ai_system' ? 'System AI' : task.createdByName || task.createdBy}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">Typ podmiotu</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Chip 
                    label={getEntityTypeLabel(task.entityType)} 
                    color={getEntityTypeColor(task.entityType)}
                    size="small"
                  />
                  {task.entityType !== 'general' && (
                    <Typography variant="body1" sx={{ ml: 1 }}>
                      {task.entityType === 'client' ? (
                        <Link href={`/clients/${task.entityId}`} sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {task.entityName || task.entityId}
                        </Link>
                      ) : (
                        <Link href={`/leads/${task.entityId}`} sx={{ display: 'flex', alignItems: 'center' }}>
                          <LeadIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {task.entityName || task.entityId}
                        </Link>
                      )}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Opis zadania
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
              {task.description}
            </Typography>
            
            {task.suggestedAction && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Sugerowana akcja
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                  {task.suggestedAction}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        {(isCompleted || isDismissed) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Informacje o {isCompleted ? 'ukończeniu' : 'odrzuceniu'} zadania
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    Data {isCompleted ? 'ukończenia' : 'odrzucenia'}
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(task.completedAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Notatki
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {task.notesOnCompletion || 'Brak notatek'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Informacje dodatkowe
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Data utworzenia</Typography>
                <Typography variant="body1">
                  {formatDate(task.createdAt)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">Ostatnia aktualizacja</Typography>
                <Typography variant="body1">
                  {formatDate(task.lastUpdate)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Dialog ukończenia zadania */}
      <Dialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Oznacz zadanie jako ukończone</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Wprowadź notatkę o wykonaniu zadania:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            placeholder="Opisz, co zostało zrobione, jaki był wynik, itp."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCompleteDialogOpen(false)} 
            color="inherit"
            disabled={loading}
          >
            Anuluj
          </Button>
          <Button 
            onClick={handleCompleteTask} 
            color="primary"
            variant="contained"
            disabled={loading || !completionNotes.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Oznacz jako ukończone'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog odrzucenia zadania */}
      <Dialog
        open={dismissDialogOpen}
        onClose={() => setDismissDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Odrzuć zadanie</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Wprowadź powód odrzucenia zadania:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            value={dismissalNotes}
            onChange={(e) => setDismissalNotes(e.target.value)}
            placeholder="Wyjaśnij, dlaczego zadanie zostało odrzucone"
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDismissDialogOpen(false)} 
            color="inherit"
            disabled={loading}
          >
            Anuluj
          </Button>
          <Button 
            onClick={handleDismissTask} 
            color="error"
            variant="contained"
            disabled={loading || !dismissalNotes.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Odrzuć zadanie'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskDetails;
